import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationJobsProvider } from './contexts/NotificationJobsContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { useAuth } from './contexts/AuthContext';
import RoleShell from './components/layout/RoleShell';
import RouteFallback from './components/common/RouteFallback';
import PageSpinner from './components/common/PageSpinner';
import { completeProfilePath, resolveCampaignId } from './lib/campaignRedirect';
import { defaultPathForRole } from './lib/roles';

const ConfirmSignupPage = lazy(() => import('./pages/auth/ConfirmSignupPage'));
const CompleteFirstLoginPage = lazy(() => import('./pages/auth/CompleteFirstLoginPage'));
const CompleteProfilePage = lazy(() => import('./pages/auth/CompleteProfilePage'));
const RoleChooserPage = lazy(() => import('./pages/auth/RoleChooserPage'));
const SigninPage = lazy(() => import('./pages/auth/SigninPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const EnrolPage = lazy(() => import('./pages/auth/EnrolPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const UnauthorizedPage = lazy(() => import('./pages/shared/UnauthorizedPage'));
const SuperAdminDashboardPage = lazy(() => import('./pages/superadmin/DashboardPage'));
const OrganisationsPage = lazy(() => import('./pages/superadmin/OrganisationsPage'));
const SuperAdminCampaignsPage = lazy(() => import('./pages/superadmin/CampaignsPage'));
const AssessmentModelsPage = lazy(() => import('./pages/superadmin/AssessmentModelsPage'));
const SuperAdminUsersPage = lazy(() => import('./pages/superadmin/UsersPage'));
const SuperAdminAnalyticsPage = lazy(() => import('./pages/superadmin/AnalyticsPage'));
const SuperAdminSettingsPage = lazy(() => import('./pages/superadmin/SettingsPage'));
const SuperAdminOrganisationWorkspacePage = lazy(() => import('./pages/superadmin/OrganisationWorkspacePage'));
const OrgAdminDashboardPage = lazy(() => import('./pages/orgadmin/DashboardPage'));
const OrgAdminCampaignsPage = lazy(() => import('./pages/orgadmin/CampaignsPage'));
const OrgAdminEmployeesPage = lazy(() => import('./pages/orgadmin/EmployeesPage'));
const OrgAdminResultsPage = lazy(() => import('./pages/orgadmin/ResultsPage'));
const OrgAdminReportsPage = lazy(() => import('./pages/orgadmin/ReportsPage'));
const ReportPreviewPage = lazy(() => import('./pages/orgadmin/ReportPreviewPage'));
const OrgManagerDashboardPage = lazy(() => import('./pages/orgmanager/DashboardPage'));
const OrgManagerResultsPage = lazy(() => import('./pages/orgmanager/ResultsPage'));
const EmployeeAssessmentPage = lazy(() => import('./pages/employee/AssessmentPage'));
const EmployeeResultsPage = lazy(() => import('./pages/employee/ResultsPage'));
const EmployeeActionPlanPage = lazy(() => import('./pages/employee/ActionPlanPage'));
const EmployeeHistoryPage = lazy(() => import('./pages/employee/HistoryPage'));
const EmployeeHelpPage = lazy(() => import('./pages/employee/HelpPage'));
const OrgAdminSettingsPage = lazy(() => import('./pages/orgadmin/OrgAdminSettingsPage'));
const SettingsPage = lazy(() => import('./pages/shared/SettingsPage'));

function lazyElement(node: React.ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>;
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationJobsProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/enrol" element={lazyElement(<EnrolPage />)} />
          <Route path="/signup" element={lazyElement(<SignupPage />)} />
          <Route path="/signup/confirm" element={lazyElement(<ConfirmSignupPage />)} />
          <Route path="/signin" element={lazyElement(<SigninPage />)} />
          <Route path="/signin/forgot-password" element={lazyElement(<ForgotPasswordPage />)} />
          <Route path="/signin/reset-password" element={lazyElement(<ResetPasswordPage />)} />
          <Route path="/signin/complete-password" element={lazyElement(<CompleteFirstLoginPage />)} />
          <Route path="/complete-profile" element={lazyElement(<CompleteProfilePage />)} />
          <Route path="/choose-role" element={lazyElement(<RoleChooserPage />)} />
          <Route path="/unauthorized" element={<ProtectedRoute>{lazyElement(<UnauthorizedPage />)}</ProtectedRoute>} />

          <Route
            path="/super-admin"
            element={
              <ProtectedRoute roles={['superAdmin']}>
                <RoleShell
                  title="Engage to Succeed"
                  subtitle="Super Admin"
                  avatar="SA"
                  actionLabel="Add organisation"
                  actionTo="/super-admin/organisations?create=1"
                  navigation={[
                    { to: '/super-admin', label: 'Dashboard', icon: 'grid' },
                    { to: '/super-admin/organisations', label: 'Organisations', icon: 'building' },
                    { to: '/super-admin/users', label: 'Users', icon: 'users' },
                    { to: '/super-admin/campaigns', label: 'Campaigns', icon: 'flag' },
                    { to: '/super-admin/assessments', label: 'Assessment Models', icon: 'layers' },
                    { to: '/super-admin/analytics', label: 'Analytics', icon: 'bar-chart' },
                    { to: '/super-admin/settings', label: 'Settings', icon: 'settings' },
                  ]}
                />
              </ProtectedRoute>
            }
          >
            <Route index element={lazyElement(<SuperAdminDashboardPage />)} />
            <Route path="organisations/:orgId" element={lazyElement(<SuperAdminOrganisationWorkspacePage />)} />
            <Route path="organisations" element={lazyElement(<OrganisationsPage />)} />
            <Route path="users" element={lazyElement(<SuperAdminUsersPage />)} />
            <Route path="campaigns" element={lazyElement(<SuperAdminCampaignsPage />)} />
            <Route path="assessments" element={lazyElement(<AssessmentModelsPage />)} />
            <Route path="analytics" element={lazyElement(<SuperAdminAnalyticsPage />)} />
            <Route path="settings" element={lazyElement(<SuperAdminSettingsPage />)} />
          </Route>

          <Route
            path="/org-admin"
            element={
              <ProtectedRoute roles={['orgAdmin']}>
                <RoleShell
                  title="Organisation"
                  subtitle="Organisation Admin"
                  avatar="OA"
                  actionLabel="Invite employees"
                  actionTo="/org-admin/employees"
                  navigation={[
                    { to: '/org-admin', label: 'Dashboard', icon: 'grid' },
                    { to: '/org-admin/campaigns', label: 'Campaigns', icon: 'flag' },
                    { to: '/org-admin/employees', label: 'Employees', icon: 'users' },
                    { to: '/org-admin/results', label: 'Results', icon: 'bar-chart' },
                    { to: '/org-admin/reports', label: 'Reports', icon: 'file' },
                    { to: '/org-admin/settings', label: 'Settings', icon: 'settings' },
                  ]}
                />
              </ProtectedRoute>
            }
          >
            <Route index element={lazyElement(<OrgAdminDashboardPage />)} />
            <Route path="campaigns" element={lazyElement(<OrgAdminCampaignsPage />)} />
            <Route path="employees" element={lazyElement(<OrgAdminEmployeesPage />)} />
            <Route path="results" element={lazyElement(<OrgAdminResultsPage />)} />
            <Route path="reports" element={lazyElement(<OrgAdminReportsPage />)} />
            <Route path="reports/preview" element={lazyElement(<ReportPreviewPage />)} />
            <Route path="settings" element={lazyElement(<OrgAdminSettingsPage />)} />
          </Route>

          <Route
            path="/employee"
            element={
              <ProtectedRoute roles={['employee']}>
                <RoleShell
                  title="My Workspace"
                  subtitle="Employee Portal"
                  avatar="EP"
                  navigation={[
                    { to: '/employee', label: 'Assessment', icon: 'clipboard' },
                    { to: '/employee/results', label: 'Results', icon: 'star' },
                    { to: '/employee/action-plan', label: 'Action Plan', icon: 'zap' },
                    { to: '/employee/history', label: 'History', icon: 'clock' },
                    { to: '/employee/help', label: 'Help', icon: 'help-circle' },
                    { to: '/employee/settings', label: 'Settings', icon: 'settings' },
                  ]}
                />
              </ProtectedRoute>
            }
          >
            <Route index element={lazyElement(<EmployeeAssessmentPage />)} />
            <Route path="results" element={lazyElement(<EmployeeResultsPage />)} />
            <Route path="action-plan" element={lazyElement(<EmployeeActionPlanPage />)} />
            <Route path="history" element={lazyElement(<EmployeeHistoryPage />)} />
            <Route path="help" element={lazyElement(<EmployeeHelpPage />)} />
            <Route path="settings" element={lazyElement(<SettingsPage />)} />
          </Route>

          <Route
            path="/org-manager"
            element={
              <ProtectedRoute roles={['orgManager']}>
                <RoleShell
                  title="Team Workspace"
                  subtitle="Org Manager"
                  avatar="OM"
                  navigation={[
                    { to: '/org-manager', label: 'Dashboard', icon: 'grid' },
                    { to: '/org-manager/results', label: 'Results', icon: 'bar-chart' },
                    { to: '/org-manager/reports', label: 'Reports', icon: 'file' },
                    { to: '/org-manager/settings', label: 'Settings', icon: 'settings' },
                  ]}
                />
              </ProtectedRoute>
            }
          >
            <Route index element={lazyElement(<OrgManagerDashboardPage />)} />
            <Route path="results" element={lazyElement(<OrgManagerResultsPage />)} />
            <Route path="reports" element={lazyElement(<OrgAdminReportsPage />)} />
            <Route path="reports/preview" element={lazyElement(<ReportPreviewPage />)} />
            <Route path="settings" element={lazyElement(<SettingsPage />)} />
          </Route>

          <Route path="/platform" element={<AuthLandingRedirect />} />
          <Route path="/dashboard" element={<AuthLandingRedirect />} />

          <Route
            path="/"
            element={
              <AuthLandingRedirect />
            }
          />

          <Route
            path="*"
            element={
              <AuthLandingRedirect />
            }
          />
        </Routes>
      </BrowserRouter>
      </NotificationJobsProvider>
    </AuthProvider>
  );
}

function AuthLandingRedirect() {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <PageSpinner message="Loading workspace..." />
    );
  }

  const needsProfile = user?.availableRoles.some((role) => role === 'orgAdmin' || role === 'orgManager' || role === 'employee') && user?.profileComplete === false;
  const campaignId = typeof window !== 'undefined' ? resolveCampaignId(window.location.search) : '';
  return (
    <Navigate
      to={
        isAuthenticated
          ? needsProfile
            ? completeProfilePath(campaignId)
            : user?.availableRoles.length && user.availableRoles.length > 1 && !user.activeRole
              ? '/choose-role'
              : defaultPathForRole(user?.role ?? null)
          : '/signin'
      }
      replace
    />
  );
}
